import { memo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  isUnsafeAdminApiMessage,
  toAdminFacingErrorMessage,
} from '../errors/toAdminFacingErrorMessage.js';
import type { ErrorStateProps } from './types/screenState.types.js';

function resolveErrorDescription(
  message: ReactNode | undefined,
  error: Error | undefined,
  unexpectedFallback: string,
): ReactNode {
  if (message !== undefined && typeof message !== 'string') {
    return message;
  }

  const candidate =
    (typeof message === 'string' ? message : undefined) ??
    error?.message ??
    unexpectedFallback;

  if (isUnsafeAdminApiMessage(candidate)) {
    return toAdminFacingErrorMessage(error ?? new Error(candidate), {
      fallback: unexpectedFallback,
    });
  }

  return candidate;
}

/**
 * Reusable error state — Spec Part 9 §11 anatomy:
 * icon + description (what went wrong) + next-step instruction + retry.
 */
export const ErrorState = memo<ErrorStateProps>(({
  error,
  onRetry,
  retryLabel,
  context,
  nextStep,
  title,
  message,
  icon,
}) => {
  const { t } = useTranslation('admin');
  const description = resolveErrorDescription(
    message,
    error,
    t('shared.unexpectedError'),
  );
  const contextSuffix = context ? ` (${context})` : '';
  const nextStepText = nextStep ?? t('shared.errorNextStep');
  const descriptionText =
    typeof description === 'string' ? description.trim() : null;
  const showNextStep =
    typeof nextStepText !== 'string' ||
    descriptionText === null ||
    nextStepText.trim() !== descriptionText;
  const resolvedIcon = icon ?? (
    <AlertTriangle
      className="h-5 w-5 text-[var(--color-an-danger)]"
      aria-hidden="true"
    />
  );

  return (
    <div
      className="flex items-start gap-4 rounded-xl border-2 border-[color-mix(in_srgb,var(--color-an-danger)_35%,var(--color-an-border))] bg-[var(--color-error-light)] p-6"
      role="alert"
      data-screen-state="error"
    >
      <div className="shrink-0" aria-hidden="true" data-testid="error-state-icon">
        {resolvedIcon}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="m-0 mb-2 text-base font-bold text-[var(--color-an-danger)]">
          {title ?? t('shared.error')}
        </h3>
        <p
          className="m-0 mb-2 text-sm leading-relaxed text-[var(--color-an-text)]"
          data-testid="error-state-description"
        >
          {description}
          {contextSuffix}
        </p>
        {showNextStep ? (
          <p
            className="m-0 mb-3 text-sm leading-relaxed text-[var(--color-an-text-muted)]"
            data-testid="error-state-next-step"
          >
            {nextStepText}
          </p>
        ) : null}
        {onRetry ? (
          <button
            onClick={onRetry}
            className="cursor-pointer rounded-lg border-0 bg-[var(--color-an-danger)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            type="button"
            data-testid="error-state-retry"
          >
            {retryLabel ?? t('shared.retry')}
          </button>
        ) : null}
      </div>
    </div>
  );
});

ErrorState.displayName = 'ErrorState';
